/**
 * Hero Slider JavaScript
 * Performance optimized, modular JS
 */

(function() {
  'use strict';

  class HeroSlider {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-slider-track]');
      this.dotsContainer = section.querySelector('[data-slider-dots]');
      this.slides = section.querySelectorAll('[data-slide-index]');
      this.currentIndex = 0;
      this.autoSlideDuration = parseInt(section.dataset.autoSlide) || 5000;
      this.autoSlideTimer = null;
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.translateX = 0;
      this.prevTranslate = 0;
      this.animationID = null;
      this.isHorizontalDrag = false;
      
      if (!this.track || this.slides.length <= 1) return;
      
      // Get dots after initialization
      this.dots = this.dotsContainer ? this.dotsContainer.querySelectorAll('[data-dot-index]') : [];
      
      this.init();
    }

    init() {
      this.setupDots();
      this.setupAutoSlide();
      this.setupDrag();
      this.setupKeyboard();
    }

    setupDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });
    }

    setupAutoSlide() {
      if (this.autoSlideDuration > 0 && this.autoSlideDuration <= 9000) {
        this.startAutoSlide();
      }
    }

    startAutoSlide() {
      this.stopAutoSlide();
      this.autoSlideTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoSlideDuration);
    }

    stopAutoSlide() {
      if (this.autoSlideTimer) {
        clearInterval(this.autoSlideTimer);
        this.autoSlideTimer = null;
      }
    }

    setupDrag() {
      const wrapper = this.section.querySelector('.hero-slider__wrapper');
      
      // Mouse events
      wrapper.addEventListener('mousedown', this.handleStart.bind(this));
      wrapper.addEventListener('mousemove', this.handleMove.bind(this));
      wrapper.addEventListener('mouseup', this.handleEnd.bind(this));
      wrapper.addEventListener('mouseleave', this.handleEnd.bind(this));
      
      // Touch events
      wrapper.addEventListener('touchstart', this.handleStart.bind(this), { passive: true });
      wrapper.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
      wrapper.addEventListener('touchend', this.handleEnd.bind(this));
      wrapper.addEventListener('touchcancel', this.handleEnd.bind(this));
    }

    getPositionX(event) {
      return event.type.includes('mouse') ? event.clientX : event.touches[0].clientX;
    }

    getPositionY(event) {
      return event.type.includes('mouse') ? event.clientY : event.touches[0].clientY;
    }

    handleStart(event) {
      this.stopAutoSlide();
      this.isDragging = true;
      this.startX = this.getPositionX(event);
      this.startY = this.getPositionY(event);
      this.isHorizontalDrag = false;
      this.prevTranslate = -this.currentIndex * this.track.offsetWidth;
      this.track.classList.add('is-dragging');
      const wrapper = this.section.querySelector('.hero-slider__wrapper');
      if (wrapper) wrapper.classList.add('is-dragging');
      this.animationID = requestAnimationFrame(this.animation.bind(this));
    }

    handleMove(event) {
      if (!this.isDragging) return;
      
      this.currentX = this.getPositionX(event);
      this.currentY = this.getPositionY(event);
      
      const deltaX = Math.abs(this.currentX - this.startX);
      const deltaY = Math.abs(this.currentY - this.startY);
      
      // Determine if this is a horizontal or vertical drag
      if (!this.isHorizontalDrag && deltaX > 10) {
        // If horizontal movement is detected, mark as horizontal drag
        this.isHorizontalDrag = true;
      }
      
      // Only prevent default if it's a horizontal drag
      // This allows vertical scrolling to work normally
      if (this.isHorizontalDrag || deltaX > deltaY) {
        event.preventDefault();
        this.translateX = this.currentX - this.startX;
      } else {
        // Vertical scroll - don't interfere
        this.isDragging = false;
        this.track.classList.remove('is-dragging');
        const wrapper = this.section.querySelector('.hero-slider__wrapper');
        if (wrapper) wrapper.classList.remove('is-dragging');
        cancelAnimationFrame(this.animationID);
        this.startAutoSlide();
      }
    }

    handleEnd() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      const wrapper = this.section.querySelector('.hero-slider__wrapper');
      if (wrapper) wrapper.classList.remove('is-dragging');
      cancelAnimationFrame(this.animationID);
      
      const movedBy = this.translateX;
      const threshold = 50;
      
      if (movedBy < -threshold && this.currentIndex < this.slides.length - 1) {
        this.nextSlide();
      } else if (movedBy > threshold && this.currentIndex > 0) {
        this.prevSlide();
      } else {
        this.setPositionByIndex();
      }
      
      this.translateX = 0;
      this.startAutoSlide();
    }

    animation() {
      this.setSliderPosition();
      if (this.isDragging) {
        this.animationID = requestAnimationFrame(this.animation.bind(this));
      }
    }

    setSliderPosition() {
      const currentTranslate = this.prevTranslate + this.translateX;
      this.track.style.transform = `translateX(${currentTranslate}px)`;
    }

    setPositionByIndex() {
      if (!this.track) return;
      const slideWidth = this.track.offsetWidth;
      this.prevTranslate = -this.currentIndex * slideWidth;
      this.track.style.transform = `translateX(${this.prevTranslate}px)`;
      this.updateDots();
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      
      this.currentIndex = index;
      this.updateDots();
      this.setPositionByIndex();
      this.startAutoSlide();
    }

    nextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    }

    prevSlide() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.goToSlide(prevIndex);
    }

    updateDots() {
      if (!this.dots || this.dots.length === 0) {
        // Re-fetch dots if not available
        this.dots = this.dotsContainer ? this.dotsContainer.querySelectorAll('[data-dot-index]') : [];
      }
      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) {
          dot.classList.add('hero-slider__dot--active');
        } else {
          dot.classList.remove('hero-slider__dot--active');
        }
      });
    }

    setupKeyboard() {
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          this.nextSlide();
        }
      });
    }
  }

  // Initialize sliders when DOM is ready
  function initHeroSliders() {
    const heroSliders = document.querySelectorAll('.hero-slider[data-section-id]');
    heroSliders.forEach(section => {
      new HeroSlider(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroSliders);
  } else {
    initHeroSliders();
  }

  // Re-initialize on section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.detail.container.querySelector('.hero-slider[data-section-id]');
      if (section) {
        new HeroSlider(section);
      }
    });
  }
})();

